// Módulo 2: ficha de campaña con tareas, dependencias, materiales, comentarios,
// aprobaciones e historial.
import * as React from "react";
import { useParams } from "@tanstack/react-router";
import { Check, ExternalLink, Plus, Sparkles } from "lucide-react";
import {
  Boton,
  Esqueleto,
  Pill,
  Select,
  SelectContenido,
  SelectDisparador,
  SelectOpcion,
  SelectValor,
  Tabla,
  TablaCabecera,
  TablaCelda,
  TablaCuerpo,
  TablaEncabezado,
  TablaFila,
  Tabs,
  TabsContenido,
  TabsDisparador,
  TabsLista,
  Textarea,
  TituloPagina,
  Vacio,
  cn,
} from "@iavmx/ui";
import { useAccion, useCatalogo, useConSesion, useEsMercadotecnia, useFicha, type Desglose, type TareaFicha } from "./consultas";
import { useSesion } from "../sesion";
import { Dato, EncabezadoSeccion, Migas, Nota, Sem } from "./componentes";
import { HistorialLista } from "./Historial";
import { DialogoDesglose, DialogoFecha, DialogoLiga, DialogoMaterial, DialogoTarea, type NuevaTarea, type NuevoMaterial } from "./FichaDialogos";
import { ETIQUETA_ENTREGABLE, ETIQUETA_TAREA, TONO_TAREA, fechaCorta, fechaHora, fechaLarga, relativo, type EstadoTarea } from "./util";

const ETIQUETA_CAMPANA: Record<string, string> = {
  planeada: "Por desglosar",
  en_proceso: "En producción",
  en_riesgo: "En producción",
  publicada: "Publicada",
  cerrada: "Cerrada",
};

export function Ficha() {
  const { id } = useParams({ strict: false }) as { id: string };
  const conSesion = useConSesion();
  const esMkt = useEsMercadotecnia();
  const { persona } = useSesion();
  const q = useFicha(id);
  const cat = useCatalogo();
  const [tab, setTab] = React.useState("tareas");
  const [dialogoTarea, setDialogoTarea] = React.useState(false);
  const [propuesta, setPropuesta] = React.useState<Desglose | null>(null);
  const [tareaFecha, setTareaFecha] = React.useState<TareaFicha | null>(null);
  const [dialogoMaterial, setDialogoMaterial] = React.useState(false);
  const [entregando, setEntregando] = React.useState<{ id: string; nombre: string; urlDrive: string | null } | null>(null);
  const [comentario, setComentario] = React.useState("");
  const invalidar = [["campana", id], "bandeja", "calendario", "historial"];

  const crearTarea = useAccion<NuevaTarea>({
    fn: (api, t) => api.api.campanas[":id"].tareas.$post({ param: { id }, json: t }),
    invalidar,
    exito: "Tarea agregada.",
    onExito: () => setDialogoTarea(false),
  });
  const proponer = useAccion<void, Desglose>({
    fn: (api) => api.api.campanas[":id"].tareas.proponer.$post({ param: { id } }),
    onExito: (r) => setPropuesta(r),
  });
  const confirmarDesglose = useAccion<Desglose["desglose"]["tareas"], { creadas: number }>({
    fn: (api, tareas) => api.api.campanas[":id"].tareas.lote.$post({ param: { id }, json: { propuestaId: propuesta?.propuestaId ?? null, tareas } }),
    invalidar,
    exito: (r) => `${r.creadas} tareas creadas con sus dependencias.`,
    onExito: () => setPropuesta(null),
  });
  const cambiarTarea = useAccion<{ tareaId: string; cambios: { estado?: EstadoTarea; fechaLimite?: string | null; motivo?: string; responsableId?: string | null } }, { pendienteDeAprobacion?: boolean }>({
    fn: (api, v) => api.api.campanas[":id"].tareas[":tareaId"].$patch({ param: { id, tareaId: v.tareaId }, json: v.cambios }),
    invalidar,
    exito: (r, v) => (r.pendienteDeAprobacion ? "Cambio de fecha pedido; Mercadotecnia lo aprueba." : v.cambios.estado === "terminado" ? "Tarea terminada." : "Guardado."),
    onExito: () => setTareaFecha(null),
  });
  const dependencia = useAccion<{ depId: string; materialRecibido?: boolean; materialValidado?: boolean }>({
    fn: (api, v) => api.api.campanas[":id"].dependencias[":depId"].$patch({ param: { id, depId: v.depId }, json: { materialRecibido: v.materialRecibido, materialValidado: v.materialValidado } }),
    invalidar,
    exito: (_, v) => (v.materialValidado ? "Material validado." : "Material marcado como recibido."),
  });
  const resolver = useAccion<{ aprobacionId: string; resultado: "aprobada" | "rechazada" }>({
    fn: (api, v) => api.api.campanas[":id"].aprobaciones[":aprobacionId"].resolver.$post({ param: { id, aprobacionId: v.aprobacionId }, json: { resultado: v.resultado } }),
    invalidar,
    exito: (_, v) => (v.resultado === "aprobada" ? "Aprobado y aplicado." : "Rechazado."),
  });
  const crearMaterial = useAccion<NuevoMaterial>({
    fn: (api, m) => api.api.campanas[":id"].entregables.$post({ param: { id }, json: { ...m, tipo: m.tipo as never } }),
    invalidar,
    exito: "Material pedido.",
    onExito: () => setDialogoMaterial(false),
  });
  const cambiarMaterial = useAccion<{ entregableId: string; urlDrive?: string; estado?: "pendiente" | "en_proceso" | "entregado" | "validado" | "rechazado" }>({
    fn: (api, v) => api.api.campanas[":id"].entregables[":entregableId"].$patch({ param: { id, entregableId: v.entregableId }, json: { urlDrive: v.urlDrive, estado: v.estado } }),
    invalidar,
    exito: (_, v) => (v.urlDrive ? "Liga guardada." : v.estado === "validado" ? "Material validado." : v.estado === "rechazado" ? "Se pidieron cambios." : "Guardado."),
    onExito: () => setEntregando(null),
  });
  const comentar = useAccion<string>({
    fn: (api, texto) => api.api.campanas[":id"].comentarios.$post({ param: { id }, json: { texto } }),
    invalidar: [["campana", id]],
    onExito: () => setComentario(""),
  });
  const cambiarEstadoCampana = useAccion<"publicada" | "cerrada" | "en_proceso">({
    fn: (api, estado) => api.api.campanas[":id"].$patch({ param: { id }, json: { estado } }),
    invalidar,
    exito: (_, e) => (e === "publicada" ? "Campaña marcada como publicada." : e === "cerrada" ? "Campaña cerrada." : "Campaña reabierta."),
  });

  if (!conSesion) return <Vacio titulo="Entra para ver la campaña" />;
  if (q.isLoading || cat.isLoading) return <Esqueleto className="h-80" />;
  if (q.error || !q.data || !cat.data) return <Vacio titulo="No se pudo cargar" texto={q.error?.message ?? cat.error?.message} />;
  const d = q.data;
  const c = d.campana;
  const cerrada = c.estado === "publicada" || c.estado === "cerrada";
  const conteo = {
    terminadas: d.tareas.filter((t) => t.estado === "terminado").length,
    enProceso: d.tareas.filter((t) => t.estado === "en_proceso").length,
    retrasadas: d.tareas.filter((t) => t.estado === "retrasado").length,
  };
  const materialesPendientes = d.entregables.filter((e) => e.estado === "pendiente" || e.estado === "en_proceso" || e.estado === "rechazado");
  const pendientes = d.aprobaciones.filter((a) => a.resultado === "pendiente");
  const puedeTocar = (t: TareaFicha) => esMkt || (persona != null && (t.responsableId === persona.id || (t.areaResponsableId != null && persona.areas.includes(cat.data!.areas.find((a) => a.id === t.areaResponsableId)?.clave ?? ""))));
  const resumen = `${d.tareas.length} ${d.tareas.length === 1 ? "tarea" : "tareas"} · ${conteo.terminadas} terminadas · ${conteo.enProceso} en proceso · ${conteo.retrasadas} ${conteo.retrasadas === 1 ? "retrasada" : "retrasadas"} · ${materialesPendientes.length} materiales pendientes`;

  const accionPrincipal =
    esMkt && !cerrada ? (
      d.tareas.length === 0 ? (
        <div className="flex flex-wrap gap-2">
          <Boton variante="contorno" onClick={() => setDialogoTarea(true)}>
            <Plus /> Agregar tarea
          </Boton>
          <Boton variante="acento" disabled={proponer.isPending} onClick={() => proponer.mutate()}>
            <Sparkles /> {proponer.isPending ? "Proponiendo…" : "Proponer desglose"}
          </Boton>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Boton variante="contorno" onClick={() => setDialogoMaterial(true)}>
            Pedir material
          </Boton>
          <Boton variante="acento" onClick={() => setDialogoTarea(true)}>
            <Plus /> Agregar tarea
          </Boton>
        </div>
      )
    ) : null;

  return (
    <>
      <Migas partes={[{ etiqueta: "Campañas", to: "/campanas" }, { etiqueta: c.nombre }]} />
      <TituloPagina descripcion={resumen} accion={accionPrincipal}>
        {c.nombre}
      </TituloPagina>
      <div className="mb-5 flex flex-wrap items-center gap-2">
        <Sem semaforo={d.estado.semaforo} />
        <Pill tono={cerrada ? "verde" : "tinta"}>{ETIQUETA_CAMPANA[c.estado] ?? c.estado}</Pill>
        <span className="font-ui text-sm text-tinta-tenue">{d.estado.motivo}</span>
      </div>

      <section className="mb-6 grid gap-4 border border-linea p-4 sm:grid-cols-2 sm:p-5 lg:grid-cols-4">
        <Dato etiqueta="Solicita">
          {d.solicitud?.areaSolicitante ?? "—"}
          {d.solicitud?.solicitante && <span className="text-tinta-tenue"> · {d.solicitud.solicitante}</span>}
        </Dato>
        <Dato etiqueta="Coordina">{d.coordinador ?? "Mercadotecnia"}</Dato>
        <Dato etiqueta="Publicación">
          {fechaLarga(c.fechaPublicacion)} <span className="text-tinta-tenue">· {relativo(d.hoy, c.fechaPublicacion)}</span>
        </Dato>
        <Dato etiqueta="Productos">{c.productos.length > 0 ? c.productos.join(", ") : "—"}</Dato>
        {c.objetivo && (
          <Dato etiqueta="Objetivo" className="sm:col-span-2 lg:col-span-4">
            {c.objetivo}
          </Dato>
        )}
        {d.solicitud && !d.solicitud.presupuestoOculto && (
          <Dato etiqueta="Presupuesto">{d.solicitud.presupuesto != null ? `$${Number(d.solicitud.presupuesto).toLocaleString("es-MX")} MXN` : "Sin definir"}</Dato>
        )}
      </section>

      {pendientes.map((a) => {
        const area = cat.data!.areas.find((x) => x.id === a.areaValidadoraId);
        const puede = persona != null && (persona.rol === "admin" || persona.rol === "direccion" || (area ? persona.areas.includes(area.clave) : esMkt));
        let detalle = a.comentario ?? "";
        if (a.objeto === "fecha") {
          try {
            const j = JSON.parse(a.comentario ?? "{}") as { fechaLimite?: string; anterior?: string | null; motivo?: string | null };
            detalle = `mover «${a.sobre ?? "la tarea"}» ${j.anterior ? `del ${fechaCorta(j.anterior)} ` : ""}al ${fechaCorta(j.fechaLimite)}${j.motivo ? `. ${j.motivo}` : ""}`;
          } catch {
            /* comentario libre */
          }
        }
        return (
          <Nota key={a.id} tono="ambar" className="mb-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-semibold text-tinta">Cambio pendiente de aprobación · {a.objeto}</p>
                <p className="mt-1">
                  {a.solicitadaPorNombre ?? "Alguien"} pide {detalle}
                </p>
                <p className="mt-1 text-xs text-tinta-tenue">
                  Valida {a.areaValidadora ?? "Mercadotecnia"} · {fechaHora(a.creadoEn, d.hoy)}
                </p>
              </div>
              {puede && (
                <div className="flex gap-2">
                  <Boton variante="contorno" tamano="sm" disabled={resolver.isPending} onClick={() => resolver.mutate({ aprobacionId: a.id, resultado: "rechazada" })}>
                    Rechazar
                  </Boton>
                  <Boton variante="primario" tamano="sm" disabled={resolver.isPending} onClick={() => resolver.mutate({ aprobacionId: a.id, resultado: "aprobada" })}>
                    Aprobar {a.objeto === "fecha" ? "cambio de fecha" : ""}
                  </Boton>
                </div>
              )}
            </div>
          </Nota>
        );
      })}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsLista className="h-auto w-full overflow-x-auto md:h-10">
          <TabsDisparador value="tareas" className="min-h-11 whitespace-nowrap md:min-h-0">
            <span className="md:hidden">Tareas</span>
            <span className="hidden md:inline">Tareas y dependencias</span>
          </TabsDisparador>
          <TabsDisparador value="materiales" className="min-h-11 whitespace-nowrap md:min-h-0">
            Materiales{materialesPendientes.length > 0 ? ` (${materialesPendientes.length})` : ""}
          </TabsDisparador>
          <TabsDisparador value="comentarios" className="min-h-11 whitespace-nowrap md:min-h-0">
            Comentarios{d.comentarios.length > 0 ? ` (${d.comentarios.length})` : ""}
          </TabsDisparador>
          <TabsDisparador value="historial" className="min-h-11 whitespace-nowrap md:min-h-0">
            Historial
          </TabsDisparador>
        </TabsLista>

        <TabsContenido value="tareas">
          {d.tareas.length === 0 ? (
            <Vacio
              titulo="Sin tareas todavía"
              texto="Pide el desglose propuesto (una tarea por entregable, con área y fecha antes de la publicación) o agrega tareas a mano con los botones de arriba."
            />
          ) : (
            <>
              <div className="hidden md:block">
                <Tabla>
                  <TablaCabecera>
                    <TablaFila>
                      <TablaEncabezado>Tarea</TablaEncabezado>
                      <TablaEncabezado>Área · responsable</TablaEncabezado>
                      <TablaEncabezado>Fecha límite</TablaEncabezado>
                      <TablaEncabezado>Depende de</TablaEncabezado>
                      <TablaEncabezado>Estado</TablaEncabezado>
                    </TablaFila>
                  </TablaCabecera>
                  <TablaCuerpo>
                    {d.tareas.map((t) => (
                      <TablaFila key={t.id} className={cn(t.estado === "retrasado" && "bg-rojo/5")}>
                        <TablaCelda className="align-top">
                          <p className="font-semibold">{t.titulo}</p>
                          {t.descripcion && <p className="text-xs text-tinta-tenue">{t.descripcion}</p>}
                          {t.bloquea.length > 0 && t.estado !== "terminado" && (
                            <p className="text-xs text-tinta-tenue">
                              Bloquea {t.bloquea.length === 1 ? "1 tarea" : `${t.bloquea.length} tareas`}: {t.bloquea.map((b) => b.titulo).join(", ")}
                            </p>
                          )}
                        </TablaCelda>
                        <TablaCelda className="align-top">
                          <p>{t.areaResponsable ?? <span className="text-tinta-tenue">Sin área</span>}</p>
                          {t.responsable ? (
                            <p className="text-xs text-tinta-tenue">{t.responsable}</p>
                          ) : esMkt && !cerrada ? (
                            <ResponsableSelect tarea={t} personas={cat.data!.personas} onCambiar={(rid) => cambiarTarea.mutate({ tareaId: t.id, cambios: { responsableId: rid } })} />
                          ) : null}
                        </TablaCelda>
                        <TablaCelda className="whitespace-nowrap align-top tabular-nums">
                          <p className={cn(t.estado === "retrasado" && "text-rojo")}>{fechaCorta(t.fechaLimite)}</p>
                          {t.fechaLimite && t.estado !== "terminado" && <p className="text-xs text-tinta-tenue">{relativo(d.hoy, t.fechaLimite)}</p>}
                          {!cerrada && puedeTocar(t) && t.estado !== "terminado" && (
                            <Boton variante="liga" tamano="sm" className="text-xs" onClick={() => setTareaFecha(t)}>
                              {esMkt ? "Mover" : "Pedir otra fecha"}
                            </Boton>
                          )}
                        </TablaCelda>
                        <TablaCelda className="align-top">
                          <Dependencias tarea={t} esMkt={esMkt && !cerrada} onValidar={(depId) => dependencia.mutate({ depId, materialValidado: true })} onRecibir={(depId) => dependencia.mutate({ depId, materialRecibido: true })} />
                        </TablaCelda>
                        <TablaCelda className="align-top">
                          <EstadoSelect tarea={t} deshabilitado={cerrada || !puedeTocar(t) || cambiarTarea.isPending} onCambiar={(estado) => cambiarTarea.mutate({ tareaId: t.id, cambios: { estado } })} />
                        </TablaCelda>
                      </TablaFila>
                    ))}
                  </TablaCuerpo>
                </Tabla>
              </div>
              <ul className="divide-y divide-linea border-y border-linea md:hidden">
                {d.tareas.map((t) => (
                  <li key={t.id} className="grid gap-2 py-3">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-ui text-base font-semibold">{t.titulo}</p>
                      <Pill tono={TONO_TAREA[t.estado]}>{ETIQUETA_TAREA[t.estado]}</Pill>
                    </div>
                    <p className="font-ui text-sm text-tinta-tenue">
                      {t.areaResponsable ?? "Sin área"}
                      {t.responsable ? ` · ${t.responsable}` : ""} · {t.fechaLimite ? `${t.estado === "retrasado" ? "venció" : "vence"} ${fechaCorta(t.fechaLimite)}` : "sin fecha"}
                    </p>
                    {t.dependencias.length > 0 && (
                      <p className="font-ui text-xs text-tinta-tenue">
                        Depende de {t.dependencias.map((x) => `${x.titulo}${x.materialValidado ? " (validado)" : x.materialRecibido ? " (recibido, sin validar)" : ""}`).join(", ")}
                      </p>
                    )}
                    {t.bloquea.length > 0 && t.estado !== "terminado" && <p className="font-ui text-xs text-tinta-tenue">Bloquea: {t.bloquea.map((b) => b.titulo).join(", ")}</p>}
                    {!cerrada && puedeTocar(t) && (
                      <div className="flex flex-wrap items-center gap-2">
                        <EstadoSelect tarea={t} deshabilitado={cambiarTarea.isPending} onCambiar={(estado) => cambiarTarea.mutate({ tareaId: t.id, cambios: { estado } })} />
                        {t.estado !== "terminado" && (
                          <Boton variante="contorno" tamano="sm" className="h-11" onClick={() => setTareaFecha(t)}>
                            {esMkt ? "Mover fecha" : "Pedir otra fecha"}
                          </Boton>
                        )}
                        {esMkt &&
                          t.dependencias
                            .filter((x) => x.materialRecibido && !x.materialValidado)
                            .map((x) => (
                              <Boton key={x.id} variante="primario" tamano="sm" className="h-11" onClick={() => dependencia.mutate({ depId: x.id, materialValidado: true })}>
                                <Check /> Validar {x.titulo}
                              </Boton>
                            ))}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </TabsContenido>

        <TabsContenido value="materiales">
          <div className="grid gap-8 md:grid-cols-2">
            <section>
              <EncabezadoSeccion
                accion={
                  !cerrada ? (
                    <Boton variante="contorno" tamano="sm" onClick={() => setDialogoMaterial(true)}>
                      <Plus /> Pedir material
                    </Boton>
                  ) : undefined
                }
              >
                Materiales pendientes
              </EncabezadoSeccion>
              <ListaMateriales lista={materialesPendientes} hoy={d.hoy} esMkt={esMkt && !cerrada} onEntregar={(e) => setEntregando(e)} onEstado={(entregableId, estado) => cambiarMaterial.mutate({ entregableId, estado })} vacio="Nada pendiente. Todo lo pedido ya llegó." />
            </section>
            <section>
              <EncabezadoSeccion>Materiales entregados</EncabezadoSeccion>
              <ListaMateriales lista={d.entregables.filter((e) => e.estado === "entregado" || e.estado === "validado")} hoy={d.hoy} esMkt={esMkt && !cerrada} onEntregar={(e) => setEntregando(e)} onEstado={(entregableId, estado) => cambiarMaterial.mutate({ entregableId, estado })} vacio="Todavía no se entrega nada." />
            </section>
          </div>
        </TabsContenido>

        <TabsContenido value="comentarios">
          <form
            className="mb-5 grid max-w-2xl gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (comentario.trim()) comentar.mutate(comentario.trim());
            }}
          >
            <label htmlFor="comentario" className="font-ui text-xs font-semibold uppercase tracking-wide text-tinta-3">
              Comentario
            </label>
            <Textarea id="comentario" value={comentario} onChange={(e) => setComentario(e.target.value)} placeholder="Para todo el equipo de la campaña." className="min-h-20 text-base" />
            <div>
              <Boton type="submit" variante="primario" disabled={!comentario.trim() || comentar.isPending}>
                Comentar
              </Boton>
            </div>
          </form>
          {d.comentarios.length === 0 ? (
            <p className="font-ui text-sm text-tinta-tenue">Sin comentarios.</p>
          ) : (
            <ul className="divide-y divide-linea border-y border-linea">
              {d.comentarios.map((co) => (
                <li key={co.id} className="py-3">
                  <p className="font-ui text-xs text-tinta-tenue">
                    <span className="font-semibold text-tinta">{co.autor ?? "Alguien"}</span> · {fechaHora(co.creadoEn, d.hoy)}
                  </p>
                  <p className="mt-1 whitespace-pre-line font-ui text-sm text-tinta">{co.texto}</p>
                </li>
              ))}
            </ul>
          )}
        </TabsContenido>

        <TabsContenido value="historial">
          <p className="mb-3 font-ui text-sm text-tinta-tenue">Quién pidió cada cambio y quién lo aprobó.</p>
          <HistorialLista eventos={d.historial} hoy={d.hoy} />
        </TabsContenido>
      </Tabs>

      {esMkt && (
        <div className="mt-8 flex flex-wrap gap-2 border-t border-linea pt-4">
          {!cerrada ? (
            <Boton variante="contorno" disabled={cambiarEstadoCampana.isPending} onClick={() => cambiarEstadoCampana.mutate("publicada")}>
              Marcar como publicada
            </Boton>
          ) : (
            <Boton variante="contorno" disabled={cambiarEstadoCampana.isPending} onClick={() => cambiarEstadoCampana.mutate("en_proceso")}>
              Reabrir campaña
            </Boton>
          )}
        </div>
      )}

      <DialogoTarea abierto={dialogoTarea} onCerrar={() => setDialogoTarea(false)} catalogo={cat.data} tareas={d.tareas} fechaPublicacion={c.fechaPublicacion} titulo="Agregar tarea" onGuardar={(t) => crearTarea.mutate(t)} guardando={crearTarea.isPending} />
      <DialogoDesglose propuesta={propuesta} onCerrar={() => setPropuesta(null)} catalogo={cat.data} fechaPublicacion={c.fechaPublicacion} onConfirmar={(t) => confirmarDesglose.mutate(t)} guardando={confirmarDesglose.isPending} />
      <DialogoFecha tarea={tareaFecha} onCerrar={() => setTareaFecha(null)} fechaPublicacion={c.fechaPublicacion} esMercadotecnia={esMkt} onGuardar={(fecha, motivo) => tareaFecha && cambiarTarea.mutate({ tareaId: tareaFecha.id, cambios: { fechaLimite: fecha, motivo } })} guardando={cambiarTarea.isPending} />
      <DialogoMaterial abierto={dialogoMaterial} onCerrar={() => setDialogoMaterial(false)} catalogo={cat.data} tareas={d.tareas} fechaPublicacion={c.fechaPublicacion} onGuardar={(m) => crearMaterial.mutate(m)} guardando={crearMaterial.isPending} />
      <DialogoLiga entregable={entregando} onCerrar={() => setEntregando(null)} onGuardar={(url) => entregando && cambiarMaterial.mutate({ entregableId: entregando.id, urlDrive: url })} guardando={cambiarMaterial.isPending} />
    </>
  );
}

function EstadoSelect({ tarea, deshabilitado, onCambiar }: { tarea: TareaFicha; deshabilitado: boolean; onCambiar: (e: EstadoTarea) => void }) {
  const opciones: EstadoTarea[] = ["pendiente", "en_proceso", "terminado"];
  return (
    <Select value={tarea.estado} onValueChange={(v) => onCambiar(v as EstadoTarea)} disabled={deshabilitado}>
      <SelectDisparador aria-label={`Estado de ${tarea.titulo}`} className={cn("h-11 w-40 text-sm md:h-9", tarea.estado === "retrasado" && "border-rojo text-rojo", tarea.estado === "terminado" && "text-verde")}>
        <SelectValor>{ETIQUETA_TAREA[tarea.estado]}</SelectValor>
      </SelectDisparador>
      <SelectContenido>
        {tarea.estado === "retrasado" && <SelectOpcion value="retrasado">Retrasada</SelectOpcion>}
        {opciones.map((o) => (
          <SelectOpcion key={o} value={o}>
            {ETIQUETA_TAREA[o]}
            {o === "terminado" && !tarea.puedeTerminar.ok ? " (bloqueada)" : ""}
          </SelectOpcion>
        ))}
      </SelectContenido>
    </Select>
  );
}

function ResponsableSelect({ tarea, personas, onCambiar }: { tarea: TareaFicha; personas: { id: string; nombre: string; areaId: string | null }[]; onCambiar: (id: string) => void }) {
  const gente = tarea.areaResponsableId ? personas.filter((p) => p.areaId === tarea.areaResponsableId) : personas;
  return (
    <Select value="" onValueChange={onCambiar}>
      <SelectDisparador aria-label={`Asignar responsable de ${tarea.titulo}`} className="mt-1 h-8 w-44 text-xs">
        <SelectValor placeholder="Asignar persona" />
      </SelectDisparador>
      <SelectContenido>
        {gente.map((p) => (
          <SelectOpcion key={p.id} value={p.id}>
            {p.nombre}
          </SelectOpcion>
        ))}
      </SelectContenido>
    </Select>
  );
}

function Dependencias({ tarea, esMkt, onValidar, onRecibir }: { tarea: TareaFicha; esMkt: boolean; onValidar: (depId: string) => void; onRecibir: (depId: string) => void }) {
  if (tarea.dependencias.length === 0) return <span className="text-tinta-tenue">—</span>;
  return (
    <ul className="grid gap-1">
      {tarea.dependencias.map((x) => (
        <li key={x.id} className="flex flex-wrap items-center gap-1.5 text-xs">
          <span>{x.titulo}</span>
          {x.materialValidado ? (
            <Pill tono="verde">validado</Pill>
          ) : x.materialRecibido ? (
            <>
              <Pill tono="ambar">recibido, sin validar</Pill>
              {esMkt && (
                <Boton variante="liga" tamano="sm" className="text-xs" onClick={() => onValidar(x.id)}>
                  Validar
                </Boton>
              )}
            </>
          ) : (
            <>
              <Pill tono="neutro">sin recibir</Pill>
              {esMkt && (
                <Boton variante="liga" tamano="sm" className="text-xs" onClick={() => onRecibir(x.id)}>
                  Marcar recibido
                </Boton>
              )}
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

function ListaMateriales({
  lista,
  hoy,
  esMkt,
  onEntregar,
  onEstado,
  vacio,
}: {
  lista: { id: string; nombre: string; tipo: string; areaProductora: string | null; urlDrive: string | null; version: number; estado: string; fechaLimite: string | null; revisiones: number }[];
  hoy: string;
  esMkt: boolean;
  onEntregar: (e: { id: string; nombre: string; urlDrive: string | null }) => void;
  onEstado: (id: string, estado: "validado" | "rechazado") => void;
  vacio: string;
}) {
  if (lista.length === 0) return <p className="font-ui text-sm text-tinta-tenue">{vacio}</p>;
  return (
    <ul className="divide-y divide-linea border-y border-linea">
      {lista.map((e) => {
        const vencido = e.fechaLimite && e.fechaLimite < hoy && e.estado !== "validado" && e.estado !== "entregado";
        return (
          <li key={e.id} className="grid gap-1 py-3 font-ui text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-semibold">
                {e.nombre}
                {e.version > 1 && <span className="text-tinta-tenue"> · v{e.version}</span>}
              </p>
              <span className="flex items-center gap-2">
                {e.fechaLimite && <span className={cn("text-xs", vencido ? "text-rojo" : "text-tinta-tenue")}>{vencido ? "venció" : "vence"} {fechaCorta(e.fechaLimite)}</span>}
                <Pill tono={e.estado === "validado" ? "verde" : e.estado === "entregado" ? "azul" : e.estado === "rechazado" ? "rojo" : "neutro"}>{ETIQUETA_ENTREGABLE[e.estado] ?? e.estado}</Pill>
              </span>
            </div>
            <p className="text-xs text-tinta-tenue">
              Lo da {e.areaProductora ?? "sin área"}
              {e.revisiones > 0 ? ` · ${e.revisiones} ${e.revisiones === 1 ? "revisión" : "revisiones"}` : ""}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {e.urlDrive && (
                <Boton variante="liga" tamano="sm" asChild>
                  <a href={e.urlDrive} target="_blank" rel="noreferrer">
                    Abrir en Drive <ExternalLink />
                  </a>
                </Boton>
              )}
              {e.estado !== "validado" && (
                <Boton variante="contorno" tamano="sm" onClick={() => onEntregar(e)}>
                  {e.urlDrive ? "Nueva versión" : "Entregar liga"}
                </Boton>
              )}
              {esMkt && e.estado === "entregado" && (
                <>
                  <Boton variante="primario" tamano="sm" onClick={() => onEstado(e.id, "validado")}>
                    <Check /> Validar
                  </Boton>
                  <Boton variante="fantasma" tamano="sm" onClick={() => onEstado(e.id, "rechazado")}>
                    Pedir cambios
                  </Boton>
                </>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
